import { PaletteOptions } from '@material-ui/core/styles/createPalette';
import { TypographyOptions } from '@material-ui/core/styles/createTypography';
import { Overrides } from '@material-ui/core/styles/overrides';
import { createTheme } from '@material-ui/core/styles';

// Marketing name and translation in comments
const white = '#FFFFFF'; // hvid / white
const indigo = '#131947'; // indigo
const cblue = '#53759B'; // koncern blå / corporate blue
const grey = '#CAD7E0'; // grå / grey
const lblue = '#63AEDB'; // logo blå / logo blue
//const aubergine = '#B4879B'; // aubergine / eggplant
const red = '#A01E50'; // rød / red
//const lgrey     = '#F0F2F2'; // lys grå / light grey
//const green     = '#3A9B91'; // grøn / green
const bggrey = '#F7F8F7'; // No name, used for background

function getPalette(): PaletteOptions {
    return {
        common: {
            black: '#003468',
            white: white,
        },
        background: {
            paper: white,
            default: '#fafafa',
        },
        primary: {
            light: cblue,
            main: 'rgb(34,54,96)', // #223660
            dark: indigo,
            contrastText: white,
        },
        secondary: {
            light: '#ff4081',
            main: '#f50057',
            dark: '#c51162',
            contrastText: white,
        },
        error: {
            light: '#e57373',
            main: red,
            dark: '#d32f2f',
            contrastText: white,
        },
        text: {
            primary: indigo,
            secondary: cblue,
            disabled: 'rgba(0, 0, 0, 0.38)',
            hint: 'rgba(0, 0, 0, 0.38)',
        },
    };
}

function getTypographyStyling(): TypographyOptions {
    return {
        fontFamily: ['Interstate', 'Helvetica Neue', 'Helvetica', 'Arial', 'sans-serif'].join(','),
        htmlFontSize: 16,
        subtitle1: {
            fontSize: '1.25rem',
            lineHeight: '1.5rem',
            fontWeight: 300,
        },
        h1: {
            fontSize: '1.25rem',
            lineHeight: '24px',
            fontWeight: 600,
        },
        body1: {
            fontSize: '1rem',
            lineHeight: '20px',
            fontWeight: 300,
        },
        body2: {
            fontSize: '1rem',
            lineHeight: '20px',
            fontWeight: 600,
        },
    };
}

function getOverrideStyles(): Overrides {
    return {
        MuiTypography: {
            paragraph: {
                marginBottom: 20,
            },
        },
        MuiCheckbox: {
            root: {
                marginRight: 13,
            },
        },
        MuiFormControl: {
            marginNormal: {
                marginTop: 0,
                marginBottom: 20,
            },
        },
        MuiFormLabel: {
            root: {
                fontSize: '1.25rem',
                '&.Mui-focused': {
                    color: cblue,
                },
                '&$disabled': {
                    backgroundColor: bggrey,
                    color: indigo,
                },
            },
        },
        MuiFormHelperText: {
            root: {
                marginLeft: 35,
            },
        },
        MuiFormControlLabel: {
            root: {
                marginBottom: 10,
                marginRight: 0,
                alignItems: 'flex-start',
            },
            label: {
                paddingTop: 5,
            },
        },
        MuiInput: {
            input: {
                backgroundColor: white,
                fontWeight: 400,
                alignSelf: 'flex-end',
                padding: '20px 35px 6px',
            },
            formControl: {
                'label + &': {
                    marginTop: 0,
                },
            },
            underline: {
                backgroundColor: white,
                '&:before': {
                    borderBottom: '2px solid #53759B',
                },
                '&:after': {
                    borderBottom: '2px solid #003468',
                },
                '&.Mui-disabled:before': {
                    borderBottom: '2px solid #53759B',
                },
                '&.Mui-disabled': { backgroundColor: bggrey },
            },
        },
        MuiInputBase: {
            root: {
                fontSize: '1.25rem',
            },
            input: {
                '&[disabled]': {
                    backgroundColor: bggrey,
                    color: indigo,
                },
            },
        },
        MuiInputLabel: {
            root: {
                zIndex: 1,
                display: 'block',
                transformOrigin: 'top left',
            },
            formControl: {
                transform: 'translateY(-70%)',
                left: 35,
                right: 'initial',
                top: '28px',
                pointerEvents: 'none',
            },
            shrink: {
                transform: 'translateY(-130%) scale(0.75)',
            },
        },
        MuiButton: {
            root: {
                borderRadius: 2,
                height: '50px',
                minWidth: '120px',
                textTransform: 'none',
                padding: '8px 20px',
                fontSize: '1.25rem',
                fontWeight: 300,
                marginBottom: 25,
                '& flos-icon': {
                    stroke: 'currentColor',
                    marginRight: '10px',
                },
            },
            contained: {
                backgroundColor: cblue,
                textTransform: 'none',
                boxShadow: 'none',
                color: white,
                '@media (hover: none)': {
                    backgroundColor: cblue,
                    boxShadow: 'none',
                    '&:hover': {
                        backgroundColor: `${cblue} !important`,
                        color: white,
                    },
                },
                '&:hover': {
                    backgroundColor: indigo,
                    color: white,
                    boxShadow: 'none',
                },
                '&:active': {
                    backgroundColor: indigo,
                    color: lblue,
                    boxShadow: 'none',
                },
                '&[disabled]': {
                    backgroundColor: cblue,
                    color: grey,
                    boxShadow: 'none',
                },
            },
            containedPrimary: {
                backgroundColor: indigo,
                '&:hover': {
                    color: lblue,
                    backgroundColor: indigo,
                    boxShadow: 'none',
                },
                '&:active': {
                    backgroundColor: indigo,
                    color: lblue,
                    boxShadow: 'none',
                },
                '&[disabled]': {
                    backgroundColor: indigo,
                    color: grey,
                    boxShadow: 'none',
                },
            },
            containedSecondary: {
                backgroundColor: cblue,
                '&:hover': {
                    color: lblue,
                    backgroundColor: cblue,
                    boxShadow: 'none',
                },
                '&:active': {
                    backgroundColor: cblue,
                    color: lblue,
                    boxShadow: 'none',
                },
                '&[disabled]': {
                    backgroundColor: cblue,
                    color: grey,
                    boxShadow: 'none',
                },
            },
            outlined: {
                border: '2px solid #53759B',
                padding: '8px 20px',
                color: cblue,
                '&:hover': {
                    border: '2px solid #53759B',
                    color: cblue,
                    backgroundColor: grey,
                    boxShadow: 'none',
                },
                '&:active': {
                    border: '2px solid #53759B',
                    backgroundColor: grey,
                    color: cblue,
                    boxShadow: 'none',
                },
                '&[disabled]': {
                    border: '2px solid #53759B',
                    backgroundColor: grey,
                    color: white,
                    boxShadow: 'none',
                },
            },
            outlinedPrimary: {
                border: '2px solid #131947',
                color: indigo,
                '&:hover': {
                    border: '2px solid #131947',
                    color: indigo,
                    backgroundColor: grey,
                    boxShadow: 'none',
                },
                '&:active': {
                    border: '2px solid #131947',
                    backgroundColor: grey,
                    color: indigo,
                    boxShadow: 'none',
                },
                '&[disabled]': {
                    border: '2px solid #131947',
                    backgroundColor: grey,
                    color: white,
                    boxShadow: 'none',
                },
            },
            outlinedSecondary: {
                border: '2px solid #53759B',
                color: cblue,
                '&:hover': {
                    border: '2px solid #53759B',
                    color: cblue,
                    backgroundColor: grey,
                    boxShadow: 'none',
                },
                '&:active': {
                    border: '2px solid #53759B',
                    backgroundColor: grey,
                    color: indigo,
                    boxShadow: 'none',
                },
                '&[disabled]': {
                    border: '2px solid #53759B',
                    backgroundColor: grey,
                    color: white,
                    boxShadow: 'none',
                },
            },
            fullWidth: {
                '& + .MuiButton-fullWidth': {
                    marginTop: '-20px',
                },
            },
        },
        MuiBackdrop: {
            root: {
                backgroundColor: bggrey,
                opacity: '0.2 !important',
            },
        },
        MuiDialog: {
            root: {
                backgroundColor: 'rgba(19, 25, 71, 0.2)', // same as #131947 and opacity 0.2
            },
        },
        MuiFilledInput: {
            input: {
                padding: '20px 35px 6px',
            },
        },
        MuiSelect: {
            root: {
                '&$disabled': {
                    backgroundColor: bggrey,
                    color: indigo,
                },
            },
            filled: {
                paddingTop: '13px',
                paddingBottom: '13px',
            },
            icon: {
                width: '15px',
                height: '8.6px',
            },
        },
        MuiPopover: {
            paper: {
                border: '1px solid #CAD7E0',
            },
        },
        MuiListItem: {
            root: {
                '&.Mui-selected': {
                    color: '#85C2EB',
                    backgroundColor: indigo,
                    '&:hover': {
                        color: white,
                        backgroundColor: indigo,
                    },
                },
            },
            button: {
                '&:hover': {
                    color: white,
                    backgroundColor: indigo,
                },
                '&:active': {
                    color: '#85C2EB',
                    backgroundColor: indigo,
                },
            },
        },
        MuiCircularProgress: {
            root: {
                position: 'absolute',
                left: 'calc(50% - 35px)',
            },
        },
    };
}

export const theme = createTheme({
    typography: getTypographyStyling(),
    palette: getPalette(),
    overrides: getOverrideStyles(),
});
